import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    LinearProgress
} from '@mui/material';
import { useAuth0 } from '../context/Auth0Context';
import logger from '../../utils/logger';

interface SessionTimeoutDialogProps {
    timeoutMinutes?: number;
    warningMinutes?: number;
}

const SessionTimeoutDialog: React.FC<SessionTimeoutDialogProps> = ({
    // Auth0 ID Token lifetime is 3600 seconds (60 minutes)
    // Set timeout to 50 minutes to give 10 minute buffer before token expires
    timeoutMinutes = 50,
    // Give users 5 minutes to respond to the warning
    warningMinutes = 5
}) => {
    const { isAuthenticated, logout, getAccessTokenSilently } = useAuth0();
    const [open, setOpen] = useState(false);
    const [secondsLeft, setSecondsLeft] = useState(warningMinutes * 60);
    const [isExtending, setIsExtending] = useState(false);

    // Use refs instead of state to avoid re-renders
    const lastActivityRef = useRef(Date.now());
    const timerRef = useRef<NodeJS.Timeout | null>(null);
    const warningTimerRef = useRef<NodeJS.Timeout | null>(null);
    const backupLogoutTimerRef = useRef<NodeJS.Timeout | null>(null);

    // Calculate timeout values in milliseconds
    const timeoutMs = timeoutMinutes * 60 * 1000;
    const warningMs = warningMinutes * 60 * 1000;

    // Reset the activity timer
    const resetActivityTimer = useCallback(() => {
        const now = Date.now();
        lastActivityRef.current = now;

        setOpen(false);

        if (backupLogoutTimerRef.current) {
            clearTimeout(backupLogoutTimerRef.current);
            backupLogoutTimerRef.current = null;
        }
    }, []);

    // Handle user activity
    const handleUserActivity = useCallback(() => {
        resetActivityTimer();
    }, [resetActivityTimer]);

    // Handle continue session button click
    const handleContinueSession = async () => {
        setIsExtending(true);
        logger.log('🔄 Session continuation requested by user');

        try {
            // First, try to refresh the Auth0 token to extend the actual session
            logger.log('🔄 Attempting to refresh Auth0 token...');
            const token = await getAccessTokenSilently({ cacheMode: 'off' });
            logger.log('✅ Auth0 token refreshed successfully', {
                tokenLength: token?.length || 0,
                timestamp: new Date().toISOString()
            });

            // Clear all existing timers
            if (timerRef.current) {
                clearInterval(timerRef.current);
                timerRef.current = null;
            }
            if (warningTimerRef.current) {
                clearInterval(warningTimerRef.current);
                warningTimerRef.current = null;
            }
            if (backupLogoutTimerRef.current) {
                clearTimeout(backupLogoutTimerRef.current);
                backupLogoutTimerRef.current = null;
            }

            // Reset activity tracking - this is the key fix
            const now = Date.now();
            lastActivityRef.current = now;

            // Close dialog and reset countdown
            setOpen(false);
            setSecondsLeft(warningMinutes * 60);

            // Restart activity monitoring to continue tracking user activity
            startActivityMonitoring();

            logger.log('✅ Session extended successfully', {
                newTimeout: `${timeoutMinutes} minutes`,
                warningTime: `${warningMinutes} minutes`,
                timestamp: new Date().toISOString()
            });
        } catch (error) {
            logger.error('❌ Failed to refresh Auth0 token:', {
                error: error instanceof Error ? error.message : String(error),
                errorType: error instanceof Error ? error.constructor.name : typeof error,
                stack: error instanceof Error ? error.stack : undefined
            });

            // If token refresh fails, the session is likely expired, so logout
            logger.log('⚠️ Token refresh failed - logging out user');
            logout(true);
        } finally {
            setIsExtending(false);
        }
    };

    // Handle logout button click
    const handleLogout = () => {
        logout(true);
    };

    // Start the warning countdown
    const startWarningCountdown = useCallback(() => {
        setOpen(true);
        setSecondsLeft(warningMinutes * 60);

        // Set up countdown timer
        const countdownTimer = setInterval(() => {
            setSecondsLeft(prev => {
                if (prev <= 1) {
                    // Time's up, log the user out
                    clearInterval(countdownTimer);
                    logout(true);
                    setOpen(false); // Ensure dialog is closed
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        warningTimerRef.current = countdownTimer;

        // Set a backup timeout to ensure logout happens even if the interval fails
        if (backupLogoutTimerRef.current) {
            clearTimeout(backupLogoutTimerRef.current);
        }

        backupLogoutTimerRef.current = setTimeout(() => {
            logout(true);
            setOpen(false);
        }, (warningMinutes * 60 * 1000) + 1000); // Add 1 second buffer
    }, [logout, warningMinutes]);

    // Start activity monitoring
    const startActivityMonitoring = useCallback(() => {
        // Clear any existing timer first
        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }

        // Check for inactivity
        const checkInactivity = () => {
            const now = Date.now();
            const inactiveTime = now - lastActivityRef.current;

            // If inactive for longer than timeout - warning, show warning
            // Check if dialog is currently open to avoid showing multiple dialogs
            if (inactiveTime >= timeoutMs - warningMs) {
                setOpen(currentOpen => {
                    if (!currentOpen) {
                        startWarningCountdown();
                    }
                    return currentOpen;
                });
            }
        };

        // Set up inactivity check
        const activityTimer = setInterval(checkInactivity, 10000); // Check every 10 seconds
        timerRef.current = activityTimer;
    }, [timeoutMs, warningMs, startWarningCountdown]);

    // Set up the activity monitoring
    useEffect(() => {
        if (!isAuthenticated) return;

        // Track user activity
        const events = ['mousedown', 'keypress', 'scroll', 'touchstart'];
        events.forEach(event => {
            window.addEventListener(event, handleUserActivity);
        });

        // Start monitoring
        startActivityMonitoring();

        // Cleanup
        return () => {
            events.forEach(event => {
                window.removeEventListener(event, handleUserActivity);
            });

            if (timerRef.current) {
                clearInterval(timerRef.current);
                timerRef.current = null;
            }

            if (backupLogoutTimerRef.current) {
                clearTimeout(backupLogoutTimerRef.current);
                backupLogoutTimerRef.current = null;
            }
        };
    }, [
        isAuthenticated,
        handleUserActivity,
        startActivityMonitoring
    ]);

    // Format the time remaining
    const formatTimeRemaining = () => {
        const minutes = Math.floor(secondsLeft / 60);
        const seconds = secondsLeft % 60;
        return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
    };

    // Calculate progress value (0-100)
    const progressValue = (secondsLeft / (warningMinutes * 60)) * 100;

    if (!isAuthenticated || !open) {
        return null;
    }

    return (
        <Dialog
            open={open}
            aria-labelledby="session-timeout-dialog-title"
            aria-describedby="session-timeout-dialog-description"
            disableAutoFocus={false}
            disableEnforceFocus={false}
            disableRestoreFocus={false}
        >
            <DialogTitle id="session-timeout-dialog-title">
                Session Timeout Warning
            </DialogTitle>
            <DialogContent>
                <div id="session-timeout-dialog-description">
                    <Typography variant="body1" gutterBottom>
                        Your session is about to expire due to inactivity.
                    </Typography>
                    <Typography variant="body2" color="text.secondary" paragraph>
                        You will be automatically logged out in:
                    </Typography>

                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Box sx={{ width: '100%', mr: 1 }}>
                            <LinearProgress variant="determinate" value={progressValue} />
                        </Box>
                        <Box sx={{ minWidth: 60 }}>
                            <Typography variant="body2" color="text.secondary">
                                {formatTimeRemaining()}
                            </Typography>
                        </Box>
                    </Box>

                    <Typography variant="body2">
                        Click "Continue Session" to stay logged in.
                    </Typography>
                </div>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleLogout} color="secondary">
                    Logout Now
                </Button>
                <Button
                    onClick={handleContinueSession}
                    color="primary"
                    variant="contained"
                    autoFocus
                    disabled={isExtending}
                >
                    {isExtending ? 'Extending Session...' : 'Continue Session'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default SessionTimeoutDialog;
